import { FormControl, FormField, FormItem } from '@/components/ui/form.tsx';
import { FieldErrors, useFormContext } from 'react-hook-form';
import { TPlant } from '@/features/plants/schemas/plants.schema.ts';
import { TTreeDevice } from '@/features/devices/schemas/devices.schemas.ts';
import { ReactNode, useEffect, useRef } from 'react';
import { DeviceTreeTable } from '@/features/devices/components/device-tree-table/device-tree-table.tsx';
import { useGetPlantDevicesQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-devices-plant.ts';

export const PlantFormStepEquipmentAndConnectionDetails = ({
  showTitle = false,
}: {
  showTitle?: boolean;
}) => {
  const form = useFormContext<TPlant>();
  const errorContainerRef = useRef<HTMLDivElement>(null);
  const isSubmitted = form.formState.isSubmitted;
  const hasErrors = !!form.formState.errors?.equipmentAndConnectionDetails?.devices;

  const deviceInfo = {
    plantId: form.getValues('plantId'),
    timezone: form.getValues('siteInformation.timezone.value'),
  };

  const renderDeviceErrors = (
    device: FieldErrors<TTreeDevice>,
    level: number = 0,
    parentIndex?: number
  ) => {
    if (!device || typeof device !== 'object') return null;

    const errors: ReactNode[] = [];

    for (const [key, value] of Object.entries(device) as [string, unknown][]) {
      if (value && typeof value === 'object' && 'message' in value) {
        errors.push(
          <p key={`${level}-${key}`} className="text-red-500">
            {(value as { message: string })?.message}
          </p>
        );
      }
    }

    if (device.subDevices && Array.isArray(device.subDevices)) {
      for (const [index, subDevice] of (
        device.subDevices as FieldErrors<TTreeDevice>[]
      ).entries()) {
        const subErrors = renderDeviceErrors(subDevice, level + 1, index);
        if (subErrors) {
          errors.push(
            <div key={`${level}-subDevice-${index}`} className="ml-4">
              <p className="font-medium text-gray-600">
                Subdevice Level {level + 1} - {index + 1}
                {parentIndex !== undefined && ` (Parent Device ${parentIndex + 1})`}:
              </p>
              {subErrors}
            </div>
          );
        }
      }
    }

    return errors.length > 0 ? <>{errors}</> : null;
  };

  const renderErrors = (errors: FieldErrors<TPlant>): ReactNode => {
    if (!errors?.equipmentAndConnectionDetails?.devices) return null;

    const deviceErrors: ReactNode[] = [];

    for (const [index, device] of (
      errors.equipmentAndConnectionDetails.devices as FieldErrors<TTreeDevice>[]
    ).entries()) {
      if (device) {
        const deviceError = renderDeviceErrors(device, 0, index);
        if (deviceError) {
          deviceErrors.push(
            <div key={`device-${index}`} className="mb-2">
              <p className="font-semibold text-gray-700">Device {index + 1}:</p>
              {deviceError}
            </div>
          );
        }
      }
    }

    return deviceErrors.length > 0 ? (
      <div
        ref={errorContainerRef}
        className="mt-4 space-y-2 rounded-md border border-red-200 bg-red-50 p-4"
      >
        <p className="font-semibold text-red-500">Validation Errors:</p>
        {deviceErrors}
      </div>
    ) : null;
  };

  useEffect(() => {
    if (isSubmitted && hasErrors && errorContainerRef.current) {
      errorContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      errorContainerRef.current.focus();
    }
  }, [isSubmitted, hasErrors]);

  const { isLoading: isDevicesLoading } = useGetPlantDevicesQuery({
    plantId: form.getValues('plantId'),
    enabled: Boolean(form.getValues('plantId')),
  });

  return (
    <div
      id="equipmentAndConnectionDetails"
      className="space-y-4 rounded-lg bg-white p-4 py-4 pb-8"
    >
      {showTitle && <h3 className="font-semibold">Equipment and connection details</h3>}
      <div className="space-y-4">
        <FormField
          control={form.control}
          name="equipmentAndConnectionDetails.devices"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <DeviceTreeTable
                  data={field.value}
                  setData={field.onChange}
                  deviceInfo={deviceInfo}
                  isLoading={isDevicesLoading}
                />
              </FormControl>
              {renderErrors(form.formState.errors)}
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};
